import React, {
  useState,
  FormEvent,
  SyntheticEvent,
  useEffect,
  KeyboardEvent,
} from "react";
import type { RootState } from "store";
import { useSelector, useDispatch } from "react-redux";
import { createChange } from "create_change";
import { closeItemForm as closeItemFormAction } from "redux/slices/itemFormSlice";
import { Item, ItemImage, UpdateItemChangePayload } from "types";
import { usePreventScroll } from "react-aria";
import { cacheImage, compressImage } from "helpers";
import { findItem } from "../redux/slices/checklistSlice";

export const ItemForm = () => {
  const itemToEditId = useSelector(
    (state: RootState) => state.itemForm.value.itemToEditId,
  );

  let itemToEdit = null;

  if (itemToEditId) {
    itemToEdit = useSelector((state: RootState) =>
      findItem(state.checklist, itemToEditId),
    );
  }

  const [newItemId, setNewItemId] = useState(crypto.randomUUID());

  const [disabled, setDisabled] = useState(false);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState<ItemImage[]>([]);

  useEffect(() => {
    if (itemToEdit) {
      setName(itemToEdit.name);
      setCategory(itemToEdit.category);
      setDescription(itemToEdit.description);
      setImages(itemToEdit.images || []);
    }
  }, [itemToEdit?.id]);

  usePreventScroll();

  const checklist = useSelector((state: RootState) => state.checklist.value);
  const dispatch = useDispatch();

  const categoriesList = checklist.categories.map((category) => (
    <option value={category.name} key={category.id}></option>
  ));

  function closeItemForm(event: SyntheticEvent) {
    event.preventDefault();
    dispatch(closeItemFormAction());
  }

  const updateItem = (item: Item) => {
    const payload: UpdateItemChangePayload = {
      itemId: item.id,
      name: item.name,
      images: images,
    };

    if (name !== item.name) {
      payload.newName = name;
    }

    if (category.trim() !== item.category) {
      payload.category = { old: item.category, new: category.trim() };
    }

    if (description.trim() !== item.description) {
      payload.description = { old: item.description, new: description.trim() };
    }

    return createChange({
      name: "updateItem",
      payload: payload,
    }).then(() => {
      setCategory("");
      dispatch(closeItemFormAction());
    });
  };

  const createItem = () => {
    return createChange({
      name: "createItem",
      payload: {
        itemId: newItemId,
        itemName: name,
        categoryName: category.trim(),
        description: description.trim(),
        images: images,
      },
    }).then(() => {
      setNewItemId(crypto.randomUUID());
      dispatch(closeItemFormAction());
    });
  };

  const deleteChecklistItem = (event: SyntheticEvent) => {
    event.preventDefault();
    if (itemToEdit) {
      createChange({
        name: "deleteItem",
        payload: {
          itemId: itemToEdit.id,
          itemName: itemToEdit.name,
          categoryName: itemToEdit.category,
        },
      });
    }
    dispatch(closeItemFormAction());
  };

  const handleSubmit = (event: FormEvent) => {
    setDisabled(true);
    event.preventDefault();

    (itemToEdit ? updateItem(itemToEdit) : createItem())
      .then(() => {
        setName("");
        setDescription("");
      })
      .finally(() => {
        setDisabled(false);
      });
  };

  const handleDescriptionKeyDown = (
    event: KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (!event.metaKey || event.key !== "Enter" || disabled) {
      return;
    }

    event.preventDefault();
    event.currentTarget.closest("form")?.requestSubmit();
  };

  const handleFormKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== "Escape") {
      return;
    }

    event.preventDefault();
    dispatch(closeItemFormAction());
  };

  async function imageUploadHandler(image: File) {
    const compressedImage = await compressImage(image);
    const url = await cacheImage(compressedImage);
    setImages((images) => {
      return [...images, { id: crypto.randomUUID(), url }];
    });
  }

  function handleImageChange(event: React.ChangeEvent<HTMLInputElement>) {
    Array.from(event.target.files || []).forEach((file) => {
      imageUploadHandler(file);
    });
    event.target.value = "";
  }

  function removeImage(imageId: string) {
    setImages((images) => images.filter((i) => i.id !== imageId));
  }

  return (
    <>
      <form
        id="item_form"
        className="item-form p-1"
        onSubmit={handleSubmit}
        onKeyDown={handleFormKeyDown}
      >
        <div className="d-flex mb-2 mt-1">
          <button
            type="button"
            className="btn btn-secondary me-2"
            value="Close"
            aria-label="Dismiss"
            onClick={closeItemForm}
          >
            Cancel
          </button>
          {itemToEdit && (
            <a
              onClick={deleteChecklistItem}
              title="Delete item"
              href="#"
              className="btn btn-danger"
            >
              Delete
            </a>
          )}
          <input
            type="submit"
            name="commit"
            value="Save"
            className="btn btn-primary ms-auto"
            disabled={disabled}
          />
        </div>
        <input
          className="form-control mb-3"
          type="text"
          name="name"
          id="item_name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={disabled}
          autoFocus
          placeholder={"Name"}
          autoComplete="off"
          enterKeyHint="done"
        />
        <input
          type="text"
          name="category"
          id="category"
          className="form-control mb-3"
          value={category}
          list="existing-categories"
          onChange={(e) => setCategory(e.target.value)}
          disabled={disabled}
          placeholder={"Category"}
          enterKeyHint="done"
        />
        <datalist id="existing-categories">{categoriesList}</datalist>
        <div className="mb-3">
          <input
            className="form-control mb-2"
            type="file"
            name="images"
            accept="image/*"
            multiple
            onChange={handleImageChange}
            disabled={disabled}
            aria-label="Images"
          />
          {images.length > 0 && (
            <div className="d-flex flex-wrap gap-2">
              {images.map((image) => (
                <div className="position-relative" key={image.id}>
                  <a
                    className="d-inline-block border"
                    href={image.url}
                    target="_blank"
                    aria-label="Open image"
                  >
                    <img
                      src={image.url}
                      alt=""
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        objectFit: "contain",
                      }}
                    />
                  </a>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger position-absolute top-0 end-0"
                    onClick={() => removeImage(image.id)}
                    aria-label="Delete image"
                    disabled={disabled}
                  >
                    <i className="bi-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <textarea
          className="form-control mb-3"
          name="description"
          id="description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          onKeyDown={handleDescriptionKeyDown}
          disabled={disabled}
          placeholder="Description"
          rows={8}
        />
      </form>
    </>
  );
};
